import * as React from "react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { Box, Typography, Alert, TextField } from "@mui/material";
import { addDays, startOfToday, subDays } from "date-fns";
import { uk } from "date-fns/locale";
import { fetchWithAuth } from "../../../utils";
import { buildApiUrl } from "../../../config/api";

var USER_1_KOKORIN = { id: "KOKORIN", label: "Кокорін" };
var USER_2_KOROVCHENO = { id: "KOROVCHENO", label: "Коровчено" };

const users = [USER_1_KOKORIN, USER_2_KOROVCHENO];

const makeSlotKey = (trainer, date, time) => `${trainer}|${date}|${time}`;

const splitSlotKey = (key) => {
  const [trainer, date, time] = key.split("|");
  return { trainer, date, time };
};

const isBusySlot = (requests, trainer, date, time) =>
  requests.some(
    (request) =>
      request.trainer === trainer &&
      request.date === date &&
      request.time === time &&
      request.status !== "failed"
  );

const cloneBookingData = (data = {}) =>
  Object.entries(data || {}).reduce((result, [trainer, dates]) => {
    result[trainer] = Object.entries(dates || {}).reduce(
      (dateResult, [date, times]) => {
        dateResult[date] = Array.isArray(times) ? [...times] : [];
        return dateResult;
      },
      {}
    );
    return result;
  }, {});

const removeBusyDirtySlots = (dirtySlots, requests) => {
  Object.keys(dirtySlots.current).forEach((key) => {
    const { trainer, date, time } = splitSlotKey(key);

    if (isBusySlot(requests, trainer, date, time)) {
      delete dirtySlots.current[key];
    }
  });
};

const mergeServerBookingsWithDraft = (
  serverData,
  localData,
  dirtySlots,
  requests
) => {
  const merged = cloneBookingData(serverData);

  Object.keys(dirtySlots.current).forEach((key) => {
    const { trainer, date, time } = splitSlotKey(key);

    if (isBusySlot(requests, trainer, date, time)) {
      return;
    }

    const localTimes = localData[trainer]?.[date] || [];
    const shouldBeSelected = localTimes.includes(time);

    if (!merged[trainer]) merged[trainer] = {};
    if (!merged[trainer][date]) merged[trainer][date] = [];

    if (shouldBeSelected && !merged[trainer][date].includes(time)) {
      merged[trainer][date].push(time);
    }

    if (!shouldBeSelected) {
      merged[trainer][date] = merged[trainer][date].filter(
        (item) => item !== time
      );
    }
  });

  return merged;
};

export default function BookingManager({
  selectedDate,
  setSelectedDate,
  activeUser,
  setActiveUser,
  requests,
}) {
  const today = startOfToday();
  const maxDate = addDays(today, 13);

  const allSlots = [
    "10:00",
    "10:15",
    "10:30",
    "10:45",
    "11:00",
    "11:15",
    "11:30",
    "11:45",
    "12:00",
    "12:15",
    "12:30",
    "12:45",
    "13:00",
    "13:15",
    "13:30",
    "13:45",
    "14:00",
    "14:15",
    "14:30",
    "14:45",
    "15:00",
    "15:15",
    "15:30",
    "15:45",
    "16:00",
    "16:15",
    "16:30",
    "16:45",
    "17:00",
    "17:15",
    "17:30",
    "17:45",
    "18:00",
    "18:15",
    "18:30",
    "18:45",
    "19:00",
    "19:15",
    "19:30",
    "19:45",
    "20:00",
  ];

  function formatDate(date) {
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
  }

  const [selectedTimes, setSelectedDateTimes] = React.useState({});
  const serverBookingsRef = React.useRef({});
  const dirtySlotsRef = React.useRef({});
  const requestsRef = React.useRef(requests);
  const [showAlert, setShowAlert] = React.useState(null);

  const saveSlots = async () => {
    try {
      const res = await fetchWithAuth(
        "/api/bookings/multi",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(selectedTimes),
          credentials: "include",
          mode: "cors",
        }
      );

      if (res.blockedSlots.length) {
        setShowAlert({
          id: "success",
          text: "Успішно, але перевірте " + res.blockedSlots.join(","),
        });
        setTimeout(() => {
          setShowAlert(null);
        }, 5000);
      } else {
        setShowAlert({ id: "success", text: "Успішно" });
        setTimeout(() => {
          setShowAlert(null);
        }, 1000);
      }

      serverBookingsRef.current = res.saved.data;
      dirtySlotsRef.current = {};
      setSelectedDateTimes(res.saved.data);
    } catch (err) {
      setShowAlert({ id: "error", text: "Помилка" });
      console.error(err);
      setTimeout(() => {
        setShowAlert(null);
      }, 1000);
    }
  };

  React.useEffect(() => {
    const loadBookings = () => {
      fetch(buildApiUrl("/api/bookings"))
      .then((res) => res.json())
      .then((data) => {
        serverBookingsRef.current = data;
        removeBusyDirtySlots(dirtySlotsRef, requestsRef.current);
        setSelectedDateTimes((prev) =>
          mergeServerBookingsWithDraft(
            data,
            prev,
            dirtySlotsRef,
            requestsRef.current
          )
        );
      })
      .catch((err) => console.error(err));
    };

    loadBookings();
    const intervalId = setInterval(loadBookings, 5000);

    return () => clearInterval(intervalId);
  }, []);

  React.useEffect(() => {
    requestsRef.current = requests;
    removeBusyDirtySlots(dirtySlotsRef, requests);
    setSelectedDateTimes((prev) =>
      mergeServerBookingsWithDraft(
        serverBookingsRef.current,
        prev,
        dirtySlotsRef,
        requests
      )
    );
  }, [requests]);

  const [textComment, setTextComment] = React.useState("");

  const saveText = async () => {
    await fetchWithAuth("/api/text", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text: textComment }),
      credentials: "include",
      mode: "cors",
    });
  };

  React.useEffect(() => {
    const getText = async () => {
      const res = await fetchWithAuth("/api/text", {
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        mode: "cors",
      });
      setTextComment(res.text);
    };
    getText();
  }, []);

  const minDate = subDays(today, 7);
  const selectedDateKey = formatDate(selectedDate);
  const visibleUsers =
    activeUser === null ? users : users.filter((user) => user.id === activeUser.id);
  const totalSelectedSlots = visibleUsers.reduce((total, user) => {
    return total + (selectedTimes[user.id]?.[selectedDateKey]?.length || 0);
  }, 0);
  const requestedSlotsCount = requests.filter(
    (item) =>
      item.date === selectedDateKey &&
      item.status !== "failed" &&
      (activeUser === null || item.trainer === activeUser.id)
  ).length;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={uk}>
      <section className="admin-card admin-manager">
        <div className="admin-card-header">
          <div>
            <div className="admin-section-kicker">Планування</div>
            <h2>Календар і вільні години</h2>
          </div>
          <div className="admin-inline-stats">
            <div>
              <span>Відкрито слотів</span>
              <strong>{totalSelectedSlots}</strong>
            </div>
            <div>
              <span>Активних заявок</span>
              <strong>{requestedSlotsCount}</strong>
            </div>
          </div>
        </div>

        <div className="admin-manager-grid">
          <Box className="admin-calendar-box">
            <Typography variant="h6" className="admin-subtitle">
              Оберіть дату
            </Typography>
            <DateCalendar
              value={selectedDate}
              onChange={(newDate) => setSelectedDate(newDate)}
              minDate={minDate}
              maxDate={maxDate}
            />
          </Box>

          <div className="admin-side-panel">
            <div className="admin-note-card">
              <Typography variant="h6" className="admin-subtitle">
                Текст для клієнтів
              </Typography>
              <TextField
                label="Коментар на головній"
                value={textComment}
                onChange={(e) => setTextComment(e.target.value)}
                variant="outlined"
                fullWidth
                multiline
                rows={4}
                sx={{ mb: 2 }}
              />

              <button onClick={() => saveText()} className="button">
                Зберегти текст для клієнтів
              </button>
            </div>

            <div className="admin-filter-box">
              <div className="admin-filter-title">Показати слоти для</div>
              <div className="sort-admin-users">
                <button
                  type="button"
                  onClick={() => setActiveUser(null)}
                  className={activeUser === null ? "selected" : ""}
                >
                  Всі
                </button>
                <button
                  type="button"
                  onClick={() => setActiveUser(USER_1_KOKORIN)}
                  className={activeUser?.id === USER_1_KOKORIN.id ? "selected" : ""}
                >
                  {USER_1_KOKORIN.label}
                </button>
                <button
                  type="button"
                  onClick={() => setActiveUser(USER_2_KOROVCHENO)}
                  className={
                    activeUser?.id === USER_2_KOROVCHENO.id ? "selected" : ""
                  }
                >
                  {USER_2_KOROVCHENO.label}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="admins-box-users">
          {visibleUsers.map((itemUser) => {
            const activeSlots = selectedTimes[itemUser.id]?.[selectedDateKey] || [];
            const busySlots = requests
              .filter(
                (r) =>
                  r.date === selectedDateKey &&
                  r.status !== "failed" &&
                  r.trainer === itemUser.id
              )
              .map((r) => r.time);

            return (
              <Box flex={1} key={itemUser.id} className="admin-trainer-section">
                <div className="admin-trainer-header">
                  <div className="title">{itemUser.label}</div>
                  <div className="admin-trainer-meta">
                    <span>{activeSlots.length} відкрито</span>
                    <span>{busySlots.length} зайнято</span>
                  </div>
                </div>
                <div className="button_times">
                  {allSlots.map((item) => {
                    const hasRequest = busySlots.includes(item);
                    const isSelected = activeSlots.includes(item);
                    return (
                      <div
                        key={`${itemUser.id}-${item}`}
                        onClick={() => {
                          dirtySlotsRef.current[
                            makeSlotKey(itemUser.id, selectedDateKey, item)
                          ] = true;

                          setSelectedDateTimes((prev) => {
                            const currentArray =
                              prev[itemUser.id]?.[selectedDateKey] || [];

                            let newArray;
                            if (currentArray.includes(item)) {
                              newArray = currentArray.filter((i) => i !== item);
                            } else {
                              newArray = [...currentArray, item];
                            }

                            return {
                              ...prev,
                              [itemUser.id]: {
                                ...prev[itemUser.id],
                                [selectedDateKey]: newArray,
                              },
                            };
                          });
                        }}
                        className={[
                          isSelected ? "active" : "",
                          hasRequest ? "busy" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      >
                        {item}
                      </div>
                    );
                  })}
                </div>
              </Box>
            );
          })}

          <button onClick={() => saveSlots()} className="button">
            Зберегти зміни у слотах
          </button>
        </div>
      </section>

      {showAlert?.id === "success" ? (
        <div className={"alert-position"}>
          <Alert severity="success">{showAlert.text}</Alert>
        </div>
      ) : null}
      {showAlert?.id === "error" ? (
        <div className={"alert-position"}>
          <Alert severity="error">{showAlert.text}</Alert>
        </div>
      ) : null}
    </LocalizationProvider>
  );
}
